import { durationMs } from "odyssey-shared";

/**
 * The loot toasts (P2.8-T16, ADR-091 decision 19: "each line taken shows the design's loot toast, merged within 2
 * seconds"; the design's `loot-notices` card, its `items` state: "same-item gains within 2 s merge (x4)"), for a
 * chest's take and, from P2.8-T62, a creature's drop (ADR-091's amendment for P2.8-T13, decision 6), whose copper
 * (P2.8-T39) is a toast of its own, the card's `gold` state, which the copper of every later drop joins, summed, for as
 * long as it stands and is not leaving, its time started again. A gain is shown
 * only once the server's `lootTaken` or `lootGained` names it, so a toast never states a count the bag does not hold. A
 * gain of an item joins the toast of that item from the same source last added to within `LOOT_MERGE_MS` and still
 * standing, its quantity summed and its time started again; any other gain is a toast of its own, the newest last.
 * Lines a full bag could not take are one notice (the card's `full` state), which the lost lines of another drop within
 * `LOOT_MERGE_MS` join, summed by item. At most
 * `LOOT_TOASTS_SHOWN` stand at once, the oldest giving way, since the toast column is three toasts tall. A toast
 * enters on the motion table's `toast-in`, holds for `toast-hold`, and is marked leaving for one `blink` before it is
 * gone, as the level-up toast is.
 */

/**
 * How long after a toast's last gain another gain of its item still joins it, in ms: the design's two seconds.
 */
export const LOOT_MERGE_MS = 2000;

/**
 * The most loot toasts that stand at once.
 */
export const LOOT_TOASTS_SHOWN = 3;

/**
 * Where a toast's gain came from, which keys its merge, `coin` for copper a creature's drop paid, or `full` for the
 * notice of lines a full bag could not take.
 */
export type LootSource = "chest" | "creature" | "coin" | "full";

/**
 * One line a toast names: the item's rules id and the quantity.
 */
export type LootLine = readonly [string, number];

/**
 * One toast: its key, the item's rules id and the quantity it names (empty and 0 for a `full` notice, empty and the
 * copper for a `coin` toast), where it came from, the lines a `full` notice names (none otherwise), the page clock's reading at its last gain, and whether it is
 * leaving.
 */
export interface LootToast {
  readonly id: number;
  readonly item: string;
  readonly quantity: number;
  readonly from: LootSource;
  readonly lost: readonly LootLine[];
  readonly at: number;
  readonly leaving: boolean;
}

/**
 * No lines, for a toast of a gain.
 */
const NO_LINES: readonly LootLine[] = Object.freeze([]);

/**
 * A timer: runs a call after a wait and returns its cancel.
 */
export type ToastSchedule = (fn: () => void, ms: number) => () => void;

/**
 * The toasts after a gain.
 *
 * @param toasts The toasts standing, oldest first.
 * @param item The item gained.
 * @param quantity How many.
 * @param nowMs The page clock.
 * @param id The key a new toast takes.
 * @param from Where the gain came from, a chest unless given.
 * @returns The toasts after: the gain joined to its item's standing toast from the same source when that toast's last
 *   gain was within `LOOT_MERGE_MS`, otherwise added last, and only the newest `LOOT_TOASTS_SHOWN` kept.
 * @example
 * mergeGain([{ id: 1, item: "minor_healing_potion", quantity: 2, from: "chest", lost: [], at: 1000, leaving: false }], "minor_healing_potion", 3, 2500, 2);
 * // [{ id: 1, item: "minor_healing_potion", quantity: 5, from: "chest", lost: [], at: 2500, leaving: false }]
 */
export function mergeGain(toasts: readonly LootToast[], item: string, quantity: number, nowMs: number, id: number, from: Exclude<LootSource, "full" | "coin"> = "chest"): readonly LootToast[] {
  const joins = [...toasts].reverse().find((each) => each.item === item && each.from === from && !each.leaving && nowMs - each.at <= LOOT_MERGE_MS);
  if (joins !== undefined) {
    return toasts.map((each) => (each === joins ? { ...each, quantity: each.quantity + quantity, at: nowMs } : each));
  }
  return [...toasts, { id, item, quantity, from, lost: NO_LINES, at: nowMs, leaving: false }].slice(-LOOT_TOASTS_SHOWN);
}

/**
 * The toasts after copper a creature's drop paid.
 *
 * @param toasts The toasts standing, oldest first.
 * @param copper The copper, at least 1.
 * @param nowMs The page clock.
 * @param id The key a new toast takes.
 * @returns The toasts after: the copper added to the standing `coin` toast while it is not leaving, however long
 *   since its last copper (the author's ruling of 2026-10-10: one toast keeps the running total while it shows),
 *   otherwise a toast added last, and only the newest `LOOT_TOASTS_SHOWN` kept.
 * @example
 * mergeCoin([{ id: 1, item: "", quantity: 4, from: "coin", lost: [], at: 1000, leaving: false }], 3, 2500, 2);
 * // [{ id: 1, item: "", quantity: 7, from: "coin", lost: [], at: 2500, leaving: false }]
 */
export function mergeCoin(toasts: readonly LootToast[], copper: number, nowMs: number, id: number): readonly LootToast[] {
  const joins = [...toasts].reverse().find((each) => each.from === "coin" && !each.leaving);
  if (joins !== undefined) {
    return toasts.map((each) => (each === joins ? { ...each, quantity: each.quantity + copper, at: nowMs } : each));
  }
  return [...toasts, { id, item: "", quantity: copper, from: "coin" as const, lost: NO_LINES, at: nowMs, leaving: false }].slice(-LOOT_TOASTS_SHOWN);
}

/**
 * The toasts after lines a full bag could not take.
 *
 * @param toasts The toasts standing, oldest first.
 * @param lost The lines, each an item and a quantity; at least one.
 * @param nowMs The page clock.
 * @param id The key a new notice takes.
 * @returns The toasts after: the lines joined to the standing `full` notice whose last lines came within
 *   `LOOT_MERGE_MS`, each item's quantities summed in the order first named, otherwise a notice added last, and only
 *   the newest `LOOT_TOASTS_SHOWN` kept.
 * @example
 * mergeLost([], [["minor_healing_potion", 2]], 1000, 1);
 * // [{ id: 1, item: "", quantity: 0, from: "full", lost: [["minor_healing_potion", 2]], at: 1000, leaving: false }]
 */
export function mergeLost(toasts: readonly LootToast[], lost: readonly LootLine[], nowMs: number, id: number): readonly LootToast[] {
  const joins = [...toasts].reverse().find((each) => each.from === "full" && !each.leaving && nowMs - each.at <= LOOT_MERGE_MS);
  if (joins !== undefined) {
    return toasts.map((each) => (each === joins ? { ...each, lost: sumLines(each.lost, lost), at: nowMs } : each));
  }
  return [...toasts, { id, item: "", quantity: 0, from: "full" as const, lost: sumLines(NO_LINES, lost), at: nowMs, leaving: false }].slice(-LOOT_TOASTS_SHOWN);
}

/**
 * Lines with more lines added, each item's quantities summed, in the order each item is first named.
 *
 * @param lines The lines held.
 * @param more The lines to add.
 * @returns The sums.
 */
function sumLines(lines: readonly LootLine[], more: readonly LootLine[]): readonly LootLine[] {
  const sums = new Map<string, number>(lines.map(([item, quantity]) => [item, quantity]));
  for (const [item, quantity] of more) {
    sums.set(item, (sums.get(item) ?? 0) + quantity);
  }
  return [...sums.entries()];
}

/**
 * The toasts and their timers, which the HUD's toast column reads through `subscribe` and `getSnapshot`.
 *
 * @example
 * ```ts
 * const toasts = new LootToasts((fn, ms) => { const t = setTimeout(fn, ms); return () => clearTimeout(t); }, () => performance.now());
 * toasts.show("minor_healing_potion", 2);
 * ```
 */
export class LootToasts {
  private shown: readonly LootToast[] = [];
  private serial = 0;
  private readonly listeners = new Set<() => void>();
  private readonly waits = new Map<number, () => void>();

  /**
   * @param schedule The timer the toasts' time runs on.
   * @param now The page clock, in ms.
   */
  public constructor(
    private readonly schedule: ToastSchedule,
    private readonly now: () => number,
  ) {}

  /**
   * Adds a listener for changed toasts.
   *
   * @param listener Called after each change.
   * @returns A function that removes it.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * The toasts standing, oldest first; the same list until something changes.
   *
   * @returns The toasts.
   */
  public readonly getSnapshot = (): readonly LootToast[] => this.shown;

  /**
   * Shows a gain: joined to its item's toast from the same source or as a toast of its own, whose time starts now.
   *
   * @param item The item gained.
   * @param quantity How many.
   * @param from Where it came from, a chest unless given.
   */
  public show(item: string, quantity: number, from: Exclude<LootSource, "full" | "coin"> = "chest"): void {
    this.place(mergeGain(this.shown, item, quantity, this.now(), this.serial + 1, from));
  }

  /**
   * Shows what a creature's drop gave (`lootGained`): a toast for each item moved into the bag, one for the copper it
   * paid, and the notice of the lines a full bag could not take.
   *
   * @param items The lines moved into the bag.
   * @param lost The lines lost.
   * @param coin The copper paid, 0 for none.
   */
  public gained(items: readonly LootLine[], lost: readonly LootLine[], coin = 0): void {
    for (const [item, quantity] of items) {
      this.show(item, quantity, "creature");
    }
    if (coin > 0) {
      this.place(mergeCoin(this.shown, coin, this.now(), this.serial + 1));
    }
    if (lost.length > 0) {
      this.place(mergeLost(this.shown, lost, this.now(), this.serial + 1));
    }
  }

  /**
   * Replaces the toasts with what a gain left and starts the time of the toast it touched again.
   *
   * @param after The toasts after the gain.
   */
  private place(after: readonly LootToast[]): void {
    const before = this.shown;
    const touched = after.find((each) => !before.includes(each)) as LootToast;
    if (touched.id > this.serial) {
      this.serial = touched.id;
    }
    for (const gone of before) {
      if (!after.some((each) => each.id === gone.id)) {
        this.stop(gone.id);
      }
    }
    this.set(after);
    this.stop(touched.id);
    const id = touched.id;
    const leaveAt = durationMs("toast-in") + durationMs("toast-hold");
    this.waits.set(
      id,
      this.schedule(() => {
        this.set(this.shown.map((each) => (each.id === id ? { ...each, leaving: true } : each)));
        this.waits.set(
          id,
          this.schedule(() => {
            this.waits.delete(id);
            this.set(this.shown.filter((each) => each.id !== id));
          }, durationMs("blink")),
        );
      }, leaveAt),
    );
  }

  /**
   * Removes every toast at once and stops their time, as a session's end must.
   */
  public clear(): void {
    for (const cancel of this.waits.values()) {
      cancel();
    }
    this.waits.clear();
    if (this.shown.length > 0) {
      this.set([]);
    }
  }

  /**
   * Cancels a toast's running timer, if it has one.
   *
   * @param id The toast's key.
   */
  private stop(id: number): void {
    this.waits.get(id)?.();
    this.waits.delete(id);
  }

  /**
   * Replaces the toasts and tells the listeners.
   *
   * @param next The toasts.
   */
  private set(next: readonly LootToast[]): void {
    this.shown = next;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
